import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../services/api';

const DemoTrialContext = createContext(null);

export const useDemoTrial = () => {
  const context = useContext(DemoTrialContext);
  if (!context) {
    throw new Error('useDemoTrial must be used within a DemoTrialProvider');
  }
  return context;
};

export const DemoTrialProvider = ({ children }) => {
  const navigate = useNavigate();
  
  const [demoStatus, setDemoStatus] = useState(null);
  const [isDemo, setIsDemo] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch demo status from API
  const fetchDemoStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsDemo(false);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/demo/status-by-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Check if it's a demo_expired error
        if (response.status === 403) {
          const errorData = await response.json();
          if (errorData.detail?.error === 'demo_expired') {
            setIsDemo(true);
            setIsExpired(true);
            setDemoStatus(errorData.detail);
            return;
          }
        }
        throw new Error('Failed to fetch demo status');
      }

      const data = await response.json();
      setDemoStatus(data);
      setIsDemo(data.is_demo || false);
      setIsExpired(!data.is_active && data.is_demo);
      setTimeRemainingSeconds(data.time_remaining_seconds || 0);

    } catch (err) {
      console.error('Error fetching demo status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + re-fetch whenever the token appears/changes
  // (e.g. after an SPA login that never triggered a full page reload).
  // We track the token value in state so that a login within the SPA
  // triggers a re-fetch without requiring a full page reload.
  const [tokenValue, setTokenValue] = useState(() => localStorage.getItem('token'));

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setLoading(true);
      fetchDemoStatus();
    } else {
      setIsDemo(false);
      setIsExpired(false);
      setDemoStatus(null);
      setTimeRemainingSeconds(0);
      setLoading(false);
    }
  }, [fetchDemoStatus, tokenValue]);

  // Listen for cross-tab storage changes (other tabs logging in/out)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        setTokenValue(e.newValue);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Poll for same-tab token changes (SPA login doesn't fire 'storage' in the
  // same tab). Check every 2s — cheap and only runs while the provider is
  // mounted.
  useEffect(() => {
    const interval = setInterval(() => {
      const current = localStorage.getItem('token');
      setTokenValue(prev => (prev !== current ? current : prev));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer - decrement every second
  useEffect(() => {
    if (!isDemo || isExpired || timeRemainingSeconds <= 0) return;

    const interval = setInterval(() => {
      setTimeRemainingSeconds(prev => {
        if (prev <= 1) {
          // Demo expired
          setIsExpired(true);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, isExpired]); // Note: timeRemainingSeconds intentionally omitted to avoid interval recreation

  // Redirect when demo expires
  useEffect(() => {
    if (!(isDemo && isExpired)) return;
    // Don't redirect if already on pricing
    if (window.location.pathname === '/pricing') return;

    const timer = setTimeout(() => {
      navigate('/pricing?expired=true');
    }, 3000);
    return () => clearTimeout(timer);
  }, [isDemo, isExpired, navigate]);

  // Refresh status (can be called manually)
  const refreshStatus = useCallback(() => {
    setLoading(true);
    fetchDemoStatus();
  }, [fetchDemoStatus]);

  // Get formatted time display
  const getTimeDisplay = useCallback(() => {
    if (isExpired) return 'Expired';
    
    const hours = Math.floor(timeRemainingSeconds / 3600);
    const minutes = Math.floor((timeRemainingSeconds % 3600) / 60);
    const seconds = timeRemainingSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s remaining`;
    } else {
      return `${seconds}s remaining`;
    }
  }, [timeRemainingSeconds, isExpired]);

  // Check if demo is about to expire (less than 1 hour)
  const isAboutToExpire = timeRemainingSeconds > 0 && timeRemainingSeconds < 3600;

  // Check if demo is critically low (less than 15 minutes)
  const isCriticallyLow = timeRemainingSeconds > 0 && timeRemainingSeconds < 900;

  const value = {
    demoStatus,
    isDemo,
    isExpired,
    timeRemainingSeconds,
    loading,
    error,
    refreshStatus,
    getTimeDisplay,
    isAboutToExpire,
    isCriticallyLow,
    facilityName: demoStatus?.facility_name || 'Demo Facility',
    expiresAt: demoStatus?.expires_at ? new Date(demoStatus.expires_at) : null
  };

  return (
    <DemoTrialContext.Provider value={value}>
      {children}
    </DemoTrialContext.Provider>
  );
};

export default DemoTrialContext;
